// =============================================================================
// PAIE VISITEUR (SORTANT : TEF → visiteur) — le calcul € et l'attribution
// =============================================================================
//
// SOURCE UNIQUE du barème sortant. Le cumul de la fiche, le total d'un lot
// sélectionné et la ligne « Dû au visiteur » de la grille dérivent TOUS d'ici :
// trois calculs séparés du même montant divergent toujours tôt ou tard, et
// l'écart se lirait sur un virement.
//
// ⚠️ CE MODULE NE CALCULE PAS « CE QU'ON DOIT » AU SENS COMPTABLE. Il rend un
// montant ET SA PROVENANCE. Aujourd'hui AUCUNE fiche `op_visiteurs` ne porte de
// `tarif_visite` : la totalité des montants affichés est DÉRIVÉE d'un défaut de
// 120 € décidé côté code, pas d'un barème saisi. C'est pour cela que
// `tarifEffectif` rend `source` — l'écran doit pouvoir le dire, et ne jamais
// présenter un montant dérivé comme une donnée établie.

/** Défaut appliqué quand la fiche visiteur ne porte aucun tarif. */
export const TARIF_VISITE_DEFAUT = 120;

export const SOURCE_SURCHARGE = "surcharge";  // `tarif_visite_override` du dossier
export const SOURCE_FICHE = "fiche";          // `tarif_visite` de la fiche visiteur
export const SOURCE_DEFAUT = "defaut";        // ⚠️ dérivé : personne ne l'a saisi

const LIBELLE_SOURCE = {
  [SOURCE_SURCHARGE]: "surcharge dossier",
  [SOURCE_FICHE]: "tarif de la fiche",
  [SOURCE_DEFAUT]: "dérivé du défaut",
};

export function libelleSource(source) {
  return LIBELLE_SOURCE[source] || source;
}

/**
 * Montant dû pour UNE visite, et d'où il vient.
 * Précédence : surcharge du dossier > tarif de la fiche > défaut.
 */
export function tarifEffectif(dossier, tarifFiche) {
  const surcharge = dossier?.tarif_visite_override;
  if (surcharge != null && surcharge !== "") {
    return { montant: Number(surcharge), source: SOURCE_SURCHARGE };
  }
  if (tarifFiche != null && tarifFiche !== "") {
    return { montant: Number(tarifFiche), source: SOURCE_FICHE };
  }
  return { montant: TARIF_VISITE_DEFAUT, source: SOURCE_DEFAUT };
}

/**
 * Un montant n'est ATTRIBUABLE que s'il se rattache à une prestation datée.
 *
 * ⚠️ Sans `date_visite`, on ne sait pas quelle visite serait payée : le montant
 * n'entre ni au cumul, ni au total d'un lot. La ligne reste visible et reste
 * cochable — c'est un dossier réel du visiteur — mais son montant est annoncé
 * comme NON ATTRIBUABLE plutôt que compté en douce.
 */
export function estAttribuable(dossier) {
  return Boolean(dossier?.date_visite);
}

export function estPayee(dossier) {
  return dossier?.visite_payee === true;
}

/** Une visite déjà payée n'est pas re-payable : elle sort de la sélection. */
export function estPayable(dossier) {
  return !estPayee(dossier);
}

export function idsPayables(dossiers) {
  return (dossiers || []).filter(estPayable).map(d => d.dossier_id).filter(Boolean);
}

/**
 * Cumul SORTANT de la fiche : dû / payé / reste.
 * Seules les visites ATTRIBUABLES (datées) sont comptées.
 */
export function cumulVisiteur(dossiers, tarifFiche) {
  const rows = dossiers || [];
  let du = 0, paye = 0, nonAttribuables = 0;
  for (const d of rows) {
    if (!estAttribuable(d)) {
      nonAttribuables += 1;
      continue;
    }
    const { montant } = tarifEffectif(d, tarifFiche);
    du += montant;
    if (estPayee(d)) paye += montant;
  }
  return { du, paye, reste: du - paye, nonAttribuables };
}

/**
 * Ce que représente le lot sélectionné, AVANT toute écriture.
 *
 * 🔑 C'est le chiffre que la confirmation doit citer. Il est calculé sur la
 * SÉLECTION RÉELLE, pas sur le reste-à-payer de la fiche : payer 12 visites sur
 * 42 ne coûte pas le reste dû, et annoncer l'un pour l'autre ferait valider un
 * montant qui n'est pas celui du virement.
 */
export function resumeLot(dossiers, idsSelection, tarifFiche) {
  const selection = idsSelection instanceof Set ? idsSelection : new Set(idsSelection || []);
  const rows = (dossiers || []).filter(d => selection.has(d.dossier_id));
  let montant = 0, attribuables = 0, nonAttribuables = 0;
  const sources = { [SOURCE_SURCHARGE]: 0, [SOURCE_FICHE]: 0, [SOURCE_DEFAUT]: 0 };
  for (const d of rows) {
    if (!estAttribuable(d)) {
      nonAttribuables += 1;
      continue;
    }
    const { montant: m, source } = tarifEffectif(d, tarifFiche);
    montant += m;
    attribuables += 1;
    sources[source] = (sources[source] || 0) + 1;
  }
  return { nb: rows.length, montant, attribuables, nonAttribuables, sources };
}

/** `2026-08-12` → `12/08/2026`. Rend la valeur telle quelle si non ISO. */
export function formatDateFR(iso) {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(iso || "")) return iso || "";
  const [a, m, j] = iso.split("-");
  return `${j}/${m}/${a}`;
}

export function formatEuros(v) {
  return `${Number(v || 0).toLocaleString("fr-FR")} €`;
}

/**
 * La phrase de confirmation — elle NOMME le visiteur, le montant et la date.
 *
 * ⚠️ Un « Confirmer ? » anonyme est exactement ce qui permet de payer le bon
 * montant à la mauvaise personne. La phrase est construite ici pour être la
 * même partout, et pour être testable sans monter un écran.
 */
export function phraseConfirmation({ nb, visiteur, montant, dateISO }) {
  return `Marquer ${nb} visite${nb > 1 ? "s" : ""} de ${visiteur} comme payée${nb > 1 ? "s" : ""}`
    + ` — ${formatEuros(montant)} — date du ${formatDateFR(dateISO)}`;
}
